import { Calendar, RotateCcw, Search, X } from 'lucide-react'
import React from 'react'
import apinode from '../../services/apinode' // ajuste o path se necessário
import { CustomButton } from '../../components/CustomButton'

const ModernFiltersCard = React.forwardRef(
  (
    {
      onResults, // (rows, meta) => void
      onLoadingChange, // (bool) => void
      onError, // (msg) => void
      initialPagination = '10',
      setItemsPerPage,
      orders,
      selectedOrders,
      setSelectedOrders,
    },
    ref,
  ) => {
    const [contaSelecionada, setContaSelecionada] = React.useState('all')
    const [selectedTipoPedido, setSelectedTipoPedido] = React.useState('')
    const [selectedStatus, setSelectedStatus] = React.useState('Todos')
    const [selectedTipoPesquisa, setSelectedTipoPesquisa] =
      React.useState('pedido')
    const [searchValue, setSearchValue] = React.useState('')
    const [inputValue, setInputValue] = React.useState('')

    const [isModalRangeDatePickerOpen, setIsModalRangeDatePickerOpen] =
      React.useState(false)
    const [rangeFrom, setRangeFrom] = React.useState('')
    const [rangeTo, setRangeTo] = React.useState('')

    // contas
    const [conta, setConta] = React.useState([])
    const [loadingContas, setLoadingContas] = React.useState(true)
    const [errorContas, setErrorContas] = React.useState(null)

    // loading/erro locais pra UI do botão
    const [searchLoading, setSearchLoading] = React.useState(false)
    const [searchError, setSearchError] = React.useState(null)

    const funcionario = null // troque se precisar travar por conta

    const idContaLS =
      typeof window !== 'undefined' ? localStorage.getItem('idconta') : null
    const idUsuarioLS =
      typeof window !== 'undefined' ? localStorage.getItem('idusuario') : null

    // carregar contas
    React.useEffect(() => {
      const fetchContas = async () => {
        try {
          if (!idContaLS) {
            setErrorContas('idconta não encontrado no localStorage')
            setLoadingContas(false)
            return
          }
          setLoadingContas(true)
          setErrorContas(null)
          const { data } = await apinode.get(`/pedido/contas/${idContaLS}`)
          setConta(data)
        } catch (error) {
          console.error('Erro ao buscar contas:', error)
          setErrorContas('Erro ao carregar contas')
          setConta([
            { ID_CONTA: 1, APELIDO: 'Conta Principal' },
            { ID_CONTA: 2, APELIDO: 'Conta Secundária' },
          ])
        } finally {
          setLoadingContas(false)
        }
      }
      fetchContas()
    }, [idContaLS])

    const pad = (n) => String(n).padStart(2, '0')

    // === CORREÇÃO DE FUSO: formatar a label sem criar Date a partir de 'YYYY-MM-DD'
    const formatLabel = (from, to) => {
      const fmt = (s) => {
        if (!s) return ''
        const [y, m, d] = s.split('-')
        return `${pad(d)}/${pad(m)}/${y}`
      }
      return `${fmt(from)} - ${fmt(to)}`
    }

    // Helper caso precise converter Date -> 'YYYY-MM-DD' em horário local
    const toISO = (d) => {
      const y = d.getFullYear()
      const m = pad(d.getMonth() + 1)
      const dd = pad(d.getDate())
      return `${y}-${m}-${dd}`
    }

    const aplicarRange = () => {
      if (!rangeFrom || !rangeTo) return
      setInputValue(formatLabel(rangeFrom, rangeTo)) // usa strings diretamente (sem UTC shift)
      setIsModalRangeDatePickerOpen(false)
    }

    const setQuickRange = (key) => {
      const now = new Date()
      let from, to

      if (key === 'hoje') {
        from = new Date(now)
        to = new Date(now)
      } else if (key === '7') {
        to = new Date(now)
        from = new Date(now)
        from.setDate(from.getDate() - 6)
      } else if (key === '30') {
        to = new Date(now)
        from = new Date(now)
        from.setDate(from.getDate() - 29)
      } else if (key === 'mes') {
        to = new Date(now)
        from = new Date(now.getFullYear(), now.getMonth(), 1)
      } else if (key === 'mespassado') {
        const firstCur = new Date(now.getFullYear(), now.getMonth(), 1)
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        to = new Date(firstCur - 1) // último dia do mês anterior
      }

      setRangeFrom(toISO(from))
      setRangeTo(toISO(to))
    }

    const clearFilters = () => {
      setContaSelecionada('all')
      setSelectedTipoPedido('')
      setSelectedStatus('Todos')
      setSelectedTipoPesquisa('cpf')
      setSearchValue('')
      setPagination(initialPagination)
      setRangeFrom('')
      setRangeTo('')
      setInputValue('')
      setSearchError(null)
      onError?.(null)
      onResults?.([], { total: 0 })
    }

    const buildParams = React.useCallback(
      () => ({
        [selectedTipoPesquisa]: searchValue || undefined, // cpf, cliente, pedido, nfe, ship, idpedidoloja
        startdate: rangeFrom || undefined,
        enddate: rangeTo || undefined,
        status: selectedStatus === 'Todos' ? undefined : selectedStatus,
        loja: contaSelecionada === 'all' ? undefined : contaSelecionada,
        tipo_pedido: selectedTipoPedido || undefined,
        idFuncionario: idUsuarioLS,
        pageSize: Number(initialPagination),
      }),
      [
        selectedTipoPesquisa,
        searchValue,
        rangeFrom,
        rangeTo,
        selectedStatus,
        contaSelecionada,
        selectedTipoPedido,
        idUsuarioLS,
        initialPagination,
      ],
    )

    const SearchOrders = React.useCallback(async () => {
      setSelectedOrders([])

      if (searchLoading) return
      try {
        if (!idContaLS || !idUsuarioLS) {
          const msg = 'idconta/idusuario não encontrados no localStorage'
          setSearchError(msg)
          onError?.(msg)
          return
        }

        setSearchLoading(true)
        setSearchError(null)
        onLoadingChange?.(true)

        const params = buildParams()
        const { data } = await apinode.get(`/pedido/search/${idContaLS}`, {
          params,
        })

        const rows = Array.isArray(data) ? data : data?.results || []
        const meta = { total: data?.total ?? rows.length, params }
        onResults?.(rows, meta)
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err.message ||
          'Erro ao buscar pedidos'
        setSearchError(msg)
        onError?.(msg)
        console.error('Buscar pedidos falhou:', err)
      } finally {
        setSearchLoading(false)
        onLoadingChange?.(false)
      }
    }, [
      searchLoading,
      idContaLS,
      idUsuarioLS,
      onError,
      onLoadingChange,
      onResults,
      buildParams,
    ])

    // Expor métodos ao pai (agora estável por causa do useCallback acima)
    React.useImperativeHandle(
      ref,
      () => ({
        buscar: SearchOrders,
        limpar: clearFilters,
        getParams: buildParams,
      }),
      [SearchOrders, buildParams],
    )

    const previewLabel =
      rangeFrom && rangeTo ? formatLabel(rangeFrom, rangeTo) : '—'

    return (
      <>
        <style>{`
        .filters-card {
          background: #ffffff; border-radius: 20px; padding: 32px; margin-top: 40px;
          box-shadow: 0 20px 40px rgba(0,0,0,.08); border: 1px solid rgba(0,0,0,.05);
          position: relative; overflow: hidden;
        }
        .filters-card::before { content: ''; position: absolute; top:0; left:0; right:0; height:4px;
          background: linear-gradient(135deg,#667eea 0%,#764ba2 100%); z-index: 1; }
        .filters-content { position: relative; z-index: 2; }
        .filters-row { display:flex; gap:20px; margin-bottom:20px; flex-wrap:wrap; align-items:end; }
        .filter-group { display:flex; flex-direction:column; min-width:180px; }
        .filter-label { color:#374151; font-size:14px; font-weight:600; margin-bottom:10px; letter-spacing:.025em; }
        .modern-select {
          background:#fff; border:2px solid #e5e7eb; border-radius:14px; padding:14px 18px;
          font-size:14px; font-weight:500; transition: all .3s cubic-bezier(.4,0,.2,1);
          appearance:none; background-image:url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 14px center; background-repeat:no-repeat; background-size:16px; padding-right:45px; box-shadow:0 4px 6px rgba(0,0,0,.03);
        }
        .modern-select:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 4px rgba(59,130,246,.08); transform: translateY(-1px); }
        .modern-select:hover:not(:disabled) { border-color:#d1d5db; transform: translateY(-1px); }
        .modern-select:disabled { opacity:.6; cursor:not-allowed; }
        .date-input-group { position:relative; display:flex; align-items:center; }
        .date-input {
          background:#fff; border:2px solid #e5e7eb; border-radius:14px; padding:14px 50px 14px 18px;
          font-size:14px; font-weight:500; width:300px; cursor:pointer; transition: all .3s cubic-bezier(.4,0,.2,1);
          box-shadow:0 4px 6px rgba(0,0,0,.03);
        }
        .date-input:focus { outline:none; border-color:#3b82f6; box-shadow:0 0 0 4px rgba(59,130,246,.08); transform: translateY(-1px); }
        .date-input:hover { border-color:#d1d5db; transform: translateY(-1px); }
        .date-icon { position:absolute; right:14px; color:#6b7280; pointer-events:none; }
        .clear-btn {
          background:#ef4444; border:none; border-radius:14px; width:48px; height:48px;
          display:flex; align-items:center; justify-content:center; cursor:pointer;
          transition: all .3s cubic-bezier(.4,0,.2,1); color:white; box-shadow:0 4px 6px rgba(239,68,68,.15);
        }
        .clear-btn:hover { background:#dc2626; transform: translateY(-2px); box-shadow:0 8px 15px rgba(239,68,68,.25); }
        .search-section { display:flex; gap:16px; align-items:end; flex-wrap:wrap; }
        .search-input-group {
          display:flex; background:#fff; border:2px solid #e5e7eb; border-radius:14px; overflow:hidden;
          box-shadow:0 4px 6px rgba(0,0,0,.03); transition: all .3s cubic-bezier(.4,0,.2,1);
        }
        .search-input-group:focus-within { border-color:#3b82f6; box-shadow:0 0 0 4px rgba(59,130,246,.08); transform: translateY(-1px); }
        .search-input-group:hover:not(:focus-within) { border-color:#d1d5db; transform: translateY(-1px); }
        .search-type-select { border:none; padding:14px 18px; background:#f9fafb; border-right:2px solid #e5e7eb; font-size:14px; font-weight:500; min-width:130px; appearance:none; color:#374151; }
        .search-type-select:focus { outline:none; background:#f3f4f6; }
        .search-input-field { border:none; padding:14px 18px; font-size:14px; flex:1; min-width:280px; background:transparent; color:#374151; }
        .search-input-field:focus { outline:none; }
        .search-input-field::placeholder { color:#9ca3af; }
        .search-btn {
          background:#3b82f6; border:none; padding:14px 24px; color:white; font-weight:600; cursor:pointer;
          display:flex; align-items:center; gap:8px; transition: all .3s cubic-bezier(.4,0,.2,1); border-radius:0;
        }
        .search-btn:hover { background:#2563eb; }
        .search-btn[disabled] { opacity:.6; cursor:not-allowed; }
        .btn-spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,.4); border-top:2px solid #fff; border-radius:50%; animation:spin 1s linear infinite; }
        .section-divider { height:1px; background: linear-gradient(90deg, transparent, #e5e7eb, transparent); margin:28px 0; }
        .error-text { color:#b91c1c; font-size:12px; margin-top:6px; }

        /* ===== Modal moderno de range ===== */
        .range-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:50; }
        .range-modal {
          background:#fff; border-radius:18px; padding:0; width: clamp(520px, 70vw, 860px);
          box-shadow:0 25px 60px rgba(0,0,0,.25); overflow:hidden; animation: pop .18s ease-out;
        }
        @keyframes pop { from { transform: scale(.98); opacity: .6; } to { transform: scale(1); opacity: 1); } }

        .modal-header {
          display:flex; align-items:center; justify-content:space-between;
          padding:16px 20px; border-bottom:1px solid #eef2f7;
          background: linear-gradient(135deg,#f8fafc,#f1f5f9);
        }
        .modal-title { display:flex; align-items:center; gap:8px; font-weight:700; color:#0f172a; }
        .modal-sub { font-size:12px; color:#64748b; margin-top:2px; }
        .close-btn { border:none; background:transparent; cursor:pointer; padding:6px; border-radius:8px; }
        .close-btn:hover { background:#f1f5f9; }

        .modal-body { padding:18px 20px; display:grid; gap:16px; }
        .quick-row { display:flex; gap:8px; flex-wrap:wrap; }
        .chip {
          border:1px solid #e5e7eb; background:#fff; padding:8px 12px; border-radius:999px; font-size:12px;
          cursor:pointer; transition:.2s; color:#0f172a;
        }
        .chip:hover { background:#f8fafc; border-color:#d1d5db; transform: translateY(-1px); }

        .grid-2 { display:grid; grid-template-columns: 1fr 1fr; gap:16px; }
        .field {
          border:1px solid #e5e7eb; border-radius:14px; padding:14px; background:#fff;
          display:flex; flex-direction:column; gap:8px;
        }
        .field-label { font-size:12px; color:#6b7280; font-weight:600; letter-spacing:.02em; }
        .date-ctrl {
          display:flex; align-items:center; gap:10px; border:2px solid #e5e7eb; border-radius:12px; padding:10px 12px;
          transition:.2s;
        }
        .date-ctrl:focus-within { border-color:#3b82f6; box-shadow:0 0 0 4px rgba(59,130,246,.08); }
        .date-ctrl input[type="date"] {
          border:none; outline:none; background:transparent; font-size:14px; color:#0f172a; width:100%;
        }

        .preview {
          border:1px dashed #cbd5e1; background:#f8fafc; padding:10px 12px; border-radius:12px; color:#334155; font-size:13px;
        }

        .modal-actions { display:flex; justify-content:flex-end; gap:10px; padding:14px 20px; border-top:1px solid #eef2f7; background:#fff; }
        .btn { border:none; padding:10px 14px; border-radius:12px; cursor:pointer; font-weight:600; }
        .btn-light { background:#eef2f7; color:#0f172a; }
        .btn-light:hover { background:#e2e8f0; }
        .btn-primary { background:#2563eb; color:#fff; }
        .btn-primary:disabled { opacity:.6; cursor:not-allowed; }
        .btn-primary:hover:not(:disabled) { background:#1d4ed8; }

        @media (max-width: 768px) {
          .filters-row { flex-direction: column; align-items: stretch; }
          .filter-group { min-width: auto; }
          .date-input { width: 100%; }
          .search-section { flex-direction: column; align-items: stretch; }
          .search-input-group { flex-direction: column; }
          .search-type-select { border-right: none; border-bottom: 2px solid rgba(0,0,0,.1); border-radius: 0; }
          .search-btn { border-radius: 0; }
          .search-input-field { min-width: auto; }
          .range-modal { width: 92vw; }
          .grid-2 { grid-template-columns: 1fr; }
        }

        @keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
      `}</style>

        <div className="filters-card">
          <div className="filters-content">
            <div className="filters-row">
              <div className="filter-group">
                <label className="filter-label">
                  Selecionar conta
                  {loadingContas && (
                    <span
                      className="loading-select"
                      style={{ marginLeft: 8 }}
                    >
                      ⏳
                    </span>
                  )}
                  {errorContas && (
                    <span className="error-text"> ⚠ {errorContas}</span>
                  )}
                </label>
                <select
                  className={`modern-select ${loadingContas ? 'loading-select' : ''} ${errorContas ? 'error-select' : ''}`}
                  value={contaSelecionada}
                  onChange={(e) => setContaSelecionada(e.target.value)}
                  disabled={!!funcionario?.ID_CONTA || loadingContas}
                >
                  <option value="all">
                    {loadingContas ? 'Carregando contas...' : 'Todas'}
                  </option>
                  {!loadingContas &&
                    conta.map((option) => (
                      <option
                        key={option.ID_CONTA}
                        value={option.ID_CONTA}
                      >
                        {option.APELIDO}
                      </option>
                    ))}
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Tipo de Pedido</label>
                <select
                  className="modern-select"
                  value={selectedTipoPedido}
                  onChange={(e) => setSelectedTipoPedido(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="VENDA">Venda</option>
                  <option value="REPOSICAO">Reposição</option>
                  <option value="CONDICIONAL">Condicional</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Status</label>
                <select
                  className="modern-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="Todos">Todos</option>
                  <option value="Aguardando">Aguardando</option>
                  <option value="Em aberto">Em aberto</option>
                  <option value="Atendido">Atendido</option>
                  <option value="Cancelado">Cancelado</option>
                  <option value="Ag. conf.">Ag. conferência</option>
                  <option value="Ag. Definição">Ag. Definição</option>
                  <option value="ERRO IMPRESSAO">Erro de Impressão</option>
                  <option value="Em conferência">Em conferência</option>
                  <option value="Atrasado">Atrasado</option>
                  <option value="Ag. Entrega">Ag. Entrega</option>
                  <option value="Ag. Produto">Ag. Produto</option>
                  <option value="Ag. Pagamento">Ag. Pagamento</option>
                  <option value="Pago">Pago</option>
                  <option value="Boleto">Boleto</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Selecionar Data</label>
                <div className="date-input-group">
                  <input
                    type="text"
                    className="date-input"
                    placeholder="Clique para selecionar o intervalo de datas"
                    onClick={() => setIsModalRangeDatePickerOpen(true)}
                    value={inputValue}
                    readOnly
                    aria-label="Selecionar intervalo de datas"
                  />
                  <Calendar
                    className="date-icon"
                    size={18}
                  />
                </div>
              </div>

              <button
                className="clear-btn"
                onClick={clearFilters}
                title="Limpar Filtros"
              >
                <RotateCcw size={18} />
              </button>
            </div>

            <div className="section-divider" />

            <div className="search-section">
              <div className="filter-group">
                <label className="filter-label">Buscar Pedidos</label>
                <div className="search-input-group">
                  <select
                    value={selectedTipoPesquisa}
                    onChange={(e) => setSelectedTipoPesquisa(e.target.value)}
                    className="search-type-select"
                  >
                    <option value="cpf">CPF/CNPJ</option>
                    <option value="cliente">Cliente</option>
                    <option value="pedido">Pedido</option>
                    <option value="nfe">NFe</option>
                    <option value="ship">Ship ID</option>
                    <option value="idpedidoloja">Order ID</option>
                  </select>

                  <input
                    type="text"
                    className="search-input-field"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !searchLoading) SearchOrders()
                    }}
                    placeholder={
                      selectedTipoPesquisa === 'cliente'
                        ? 'Digite o nome do cliente'
                        : `Digite o ${selectedTipoPesquisa.toUpperCase()}`
                    }
                  />

                  <button
                    className="search-btn"
                    onClick={SearchOrders}
                    disabled={searchLoading}
                  >
                    {searchLoading ? (
                      <span className="btn-spinner" />
                    ) : (
                      <Search size={16} />
                    )}
                    {searchLoading ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
                {searchError && (
                  <div className="error-text">⚠ {searchError}</div>
                )}
              </div>

              <div className="filter-group pagination-group">
                <label className="filter-label">Itens por página</label>
                <select
                  className="modern-select"
                  value={initialPagination}
                  onChange={(e) => setItemsPerPage(e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div className="filter-group pagination-group">
                <CustomButton
                  title={`${selectedOrders.length == orders.length ? 'Desmarcar' : 'Marcar'} todos os pedidos`}
                  onClick={() => {
                    if (selectedOrders.length != orders.length) {
                      for (const order of orders) {
                        const index = selectedOrders.findIndex(
                          (o) => o.PEDIDO == order.PEDIDO,
                        )

                        if (index === -1) {
                          setSelectedOrders((prevState) => [
                            ...prevState,
                            order,
                          ])
                        }
                      }
                    } else {
                      setSelectedOrders([])
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ===== Modal moderno de intervalo de datas ===== */}
        {isModalRangeDatePickerOpen && (
          <div
            className="range-backdrop"
            onClick={() => setIsModalRangeDatePickerOpen(false)}
            onKeyDown={(e) =>
              e.key === 'Escape' && setIsModalRangeDatePickerOpen(false)
            }
          >
            <div
              className="range-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div>
                  <div className="modal-title">
                    <Calendar size={18} /> Intervalo de datas
                  </div>
                  <div className="modal-sub">
                    Pré-visualização: <strong>{previewLabel}</strong>
                  </div>
                </div>
                <button
                  className="close-btn"
                  onClick={() => setIsModalRangeDatePickerOpen(false)}
                  aria-label="Fechar"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body">
                <div className="quick-row">
                  <button
                    className="chip"
                    onClick={() => setQuickRange('hoje')}
                  >
                    Hoje
                  </button>
                  <button
                    className="chip"
                    onClick={() => setQuickRange('7')}
                  >
                    Últimos 7 dias
                  </button>
                  <button
                    className="chip"
                    onClick={() => setQuickRange('30')}
                  >
                    Últimos 30 dias
                  </button>
                  <button
                    className="chip"
                    onClick={() => setQuickRange('mes')}
                  >
                    Este mês
                  </button>
                  <button
                    className="chip"
                    onClick={() => setQuickRange('mespassado')}
                  >
                    Mês passado
                  </button>
                </div>

                <div className="grid-2">
                  <div className="field">
                    <span className="field-label">De</span>
                    <div className="date-ctrl">
                      <Calendar size={16} />
                      <input
                        type="date"
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <span className="field-label">Até</span>
                    <div className="date-ctrl">
                      <Calendar size={16} />
                      <input
                        type="date"
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="preview">Selecionado: {previewLabel}</div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-light"
                  onClick={() => setIsModalRangeDatePickerOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={aplicarRange}
                  disabled={!rangeFrom || !rangeTo}
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  },
)

export default ModernFiltersCard
